import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSearch, FiUsers, FiAward, FiCalendar } from 'react-icons/fi';

const Teams = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Mock data for teams
  const teams = [
    {
      id: '1',
      name: 'Quantum Flux',
      logo: 'https://via.placeholder.com/100',
      memberCount: 32,
      category: 'fps',
      wins: 156,
      losses: 43,
      tournaments: 12,
      verified: true,
      ranking: 1,
      tagline: 'Precision under pressure',
      region: 'North America'
    },
    {
      id: '2',
      name: 'Nexus Guardians',
      logo: 'https://via.placeholder.com/100',
      memberCount: 28,
      category: 'moba',
      wins: 134,
      losses: 67,
      tournaments: 10,
      verified: true,
      ranking: 2,
      tagline: 'Defending the lane, claiming the throne',
      region: 'Europe'
    },
    {
      id: '3',
      name: 'Velocity Vipers',
      logo: 'https://via.placeholder.com/100',
      memberCount: 24,
      category: 'battle-royale',
      wins: 112,
      losses: 78,
      tournaments: 8,
      verified: true,
      ranking: 3,
      tagline: 'Strike fast, win faster',
      region: 'Asia'
    },
    {
      id: '4',
      name: 'Digital Dynasty',
      logo: 'https://via.placeholder.com/100',
      memberCount: 30,
      category: 'moba',
      wins: 103,
      losses: 56,
      tournaments: 9,
      verified: false,
      ranking: 4,
      tagline: 'Building a legacy, one match at a time',
      region: 'North America'
    },
    {
      id: '5',
      name: 'Tactical Titans',
      logo: 'https://via.placeholder.com/100',
      memberCount: 25,
      category: 'fps',
      wins: 98,
      losses: 52,
      tournaments: 7,
      verified: true,
      ranking: 5,
      tagline: 'Strategy meets skill',
      region: 'Europe'
    },
    {
      id: '6',
      name: 'Echo Enforcers',
      logo: 'https://via.placeholder.com/100',
      memberCount: 22,
      category: 'battle-royale',
      wins: 87,
      losses: 63,
      tournaments: 6,
      verified: false,
      ranking: 6,
      tagline: 'Let our victories speak for themselves',
      region: 'South America'
    },
    {
      id: '7',
      name: 'Stealth Syndicate',
      logo: 'https://via.placeholder.com/100',
      memberCount: 20,
      category: 'fps',
      wins: 78,
      losses: 47,
      tournaments: 5,
      verified: false,
      ranking: 7,
      tagline: 'You never see us coming',
      region: 'Asia'
    },
    {
      id: '8',
      name: 'Arcane Alliance',
      logo: 'https://via.placeholder.com/100',
      memberCount: 26,
      category: 'moba',
      wins: 76,
      losses: 59,
      tournaments: 6,
      verified: true,
      ranking: 8,
      tagline: 'Masters of the mystical arts',
      region: 'Oceania'
    }
  ];
  
  // Mock upcoming tournaments
  const upcomingTournaments = [
    {
      id: '1',
      title: 'Summer Championship',
      date: '2023-06-15',
      prizePool: '$50,000',
      teamsRegistered: 24,
      category: 'fps'
    },
    {
      id: '2',
      title: 'Global Invitational',
      date: '2023-07-02',
      prizePool: '$100,000',
      teamsRegistered: 16,
      category: 'moba'
    },
    {
      id: '3',
      title: 'Battle Royale Masters',
      date: '2023-07-18',
      prizePool: '$75,000',
      teamsRegistered: 20,
      category: 'battle-royale'
    }
  ];
  
  // Filter teams based on search query and category
  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         team.region.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         team.tagline.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || team.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  // Format date string
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  return (
    <div className="min-h-screen bg-harmony-blue">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-3xl font-bold text-harmony-white mb-6"
        >
          Teams
        </motion.h1>
        <p className="text-harmony-white/70 mb-6">Discover and join top esports teams</p>
      
        {/* Search and filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-harmony-white/50" />
            <input
              type="text"
              placeholder="Search teams..."
              className="w-full bg-harmony-blue-dark border border-harmony-white/10 text-harmony-white pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:border-harmony-orange"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex">
            <button 
              onClick={() => setFilterCategory('all')}
              className={`px-4 py-3 rounded-lg mr-2 ${filterCategory === 'all' ? 'bg-harmony-orange text-harmony-white' : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'}`}
            >
              All
            </button>
            <button 
              onClick={() => setFilterCategory('fps')}
              className={`px-4 py-3 rounded-lg mr-2 ${filterCategory === 'fps' ? 'bg-harmony-orange text-harmony-white' : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'}`}
            >
              FPS
            </button>
            <button 
              onClick={() => setFilterCategory('moba')}
              className={`px-4 py-3 rounded-lg mr-2 ${filterCategory === 'moba' ? 'bg-harmony-orange text-harmony-white' : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'}`}
            >
              MOBA
            </button>
            <button 
              onClick={() => setFilterCategory('battle-royale')}
              className={`px-4 py-3 rounded-lg ${filterCategory === 'battle-royale' ? 'bg-harmony-orange text-harmony-white' : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'}`}
            >
              Battle Royale
            </button>
          </div>
        </div>
      
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Teams list */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-semibold text-harmony-white mb-4">Top Teams</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredTeams.map(team => (
                <motion.div 
                  key={team.id}
                  whileHover={{ y: -5 }}
                  className="bg-harmony-blue-dark rounded-lg p-4 border border-harmony-white/10 shadow-lg"
                >
                  <div className="flex items-center mb-4">
                    <img 
                      src={team.logo} 
                      alt={team.name} 
                      className="w-16 h-16 rounded-full object-cover mr-4"
                    />
                    <div>
                      <div className="flex items-center">
                        <h3 className="text-harmony-white font-semibold">{team.name}</h3>
                        {team.verified && (
                          <span className="ml-2 bg-blue-500 text-harmony-white text-xs px-2 py-0.5 rounded-full">Verified</span>
                        )}
                      </div>
                      <p className="text-harmony-white/70 text-sm">{team.tagline}</p>
                      <p className="text-harmony-white/50 text-xs mt-1">{team.region}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-harmony-blue/30 p-2 rounded text-center">
                      <p className="text-harmony-orange font-semibold">{team.wins}</p>
                      <p className="text-harmony-white/70 text-xs">Wins</p>
                    </div>
                    <div className="bg-harmony-blue/30 p-2 rounded text-center">
                      <p className="text-red-500 font-semibold">{team.losses}</p>
                      <p className="text-harmony-white/70 text-xs">Losses</p>
                    </div>
                    <div className="bg-harmony-blue/30 p-2 rounded text-center">
                      <p className="text-harmony-orange font-semibold">{team.tournaments}</p>
                      <p className="text-harmony-white/70 text-xs">Tournaments</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex items-center text-harmony-white/70 text-sm">
                      <FiUsers className="mr-1" />
                      <span>{team.memberCount} members</span>
                    </div>
                    <Link 
                      to={`/teams/${team.id}`}
                      className="bg-harmony-orange text-harmony-white px-3 py-1 rounded-md text-sm hover:bg-harmony-orange/90"
                    >
                      View Team
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
            
            {filteredTeams.length === 0 && (
              <div className="bg-harmony-blue-dark rounded-lg p-8 text-center border border-harmony-white/10">
                <FiUsers className="mx-auto text-4xl text-harmony-white/50 mb-4" />
                <h3 className="text-harmony-white text-lg font-medium mb-2">No teams found</h3>
                <p className="text-harmony-white/70">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Tournament section */}
            <div className="bg-harmony-blue-dark rounded-lg p-4 mb-6 border border-harmony-white/10 shadow-lg">
              <h3 className="text-lg font-semibold text-harmony-white mb-4 flex items-center">
                <FiAward className="mr-2 text-harmony-orange" /> Upcoming Tournaments
              </h3>
              <div className="space-y-4">
                {upcomingTournaments.map(tournament => (
                  <div key={tournament.id} className="p-3 bg-harmony-blue/30 rounded-lg">
                    <h4 className="font-medium text-harmony-white">{tournament.title}</h4>
                    <div className="flex items-center text-harmony-white/70 text-sm mt-2">
                      <FiCalendar className="mr-1" />
                      <span>{formatDate(tournament.date)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-2">
                      <span className="text-green-500">{tournament.prizePool}</span>
                      <span className="text-harmony-white/70">{tournament.teamsRegistered} teams</span>
                    </div>
                    <Link 
                      to={`/tournaments/${tournament.id}`}
                      className="block text-center bg-harmony-orange text-harmony-white py-1 rounded mt-3 text-sm hover:bg-harmony-orange/90"
                    >
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
              <Link 
                to="/tournaments"
                className="block text-center text-harmony-orange mt-4 text-sm hover:underline"
              >
                View all tournaments →
              </Link>
            </div>
            
            {/* Create Team Button */}
            <div className="bg-harmony-blue-dark rounded-lg p-6 text-center border border-harmony-white/10 shadow-lg">
              <h3 className="text-lg font-semibold text-harmony-white mb-2">Ready to compete?</h3>
              <p className="text-harmony-white/70 text-sm mb-4">Create your own team and participate in tournaments</p>
              <Link 
                to="/teams/create"
                className="block bg-harmony-orange text-harmony-white py-2 rounded font-medium hover:bg-harmony-orange/90"
              >
                Create Team
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Teams; 